import { FC, ReactNode, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { taxColumnsGridSx } from "../helpers/taxColumnsGrid";
import { TaxFormRow, TaxRowErrors } from "../helpers/useManageCollectableTaxes";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";

type TaxRowField = "name" | "rate" | "taxRegistrationNumber";

const DATA_TEST_SUFFIX: Record<TaxRowField, string> = {
  name: "name",
  rate: "rate",
  taxRegistrationNumber: "tax-id",
};

interface TaxRowFieldsProps {
  row: Pick<TaxFormRow, TaxRowField> & { key: string };
  errors: TaxRowErrors;
  onChange: (patch: Partial<Pick<TaxFormRow, TaxRowField>>) => void;
  endAction?: ReactNode;
  showLabels?: boolean;
  forceShowErrors?: boolean;
  dataTestPrefix?: string;
}

export const TaxRowFields: FC<TaxRowFieldsProps> = ({
  row,
  errors,
  onChange,
  endAction,
  showLabels = false,
  forceShowErrors = false,
  dataTestPrefix = "tax-row",
}) => {
  const { t } = useTranslate();
  const [touchedFields, setTouchedFields] = useState<Partial<Record<TaxRowField, boolean>>>({});

  const markTouched = (field: TaxRowField) => setTouchedFields((fields) => ({ ...fields, [field]: true }));
  const visibleError = (field: TaxRowField): string | undefined =>
    forceShowErrors || touchedFields[field] ? errors[field] : undefined;

  const labels: Record<TaxRowField, string> = {
    name: t("dashboard", "taxes.manage.taxName"),
    rate: t("dashboard", "taxes.manage.taxRate"),
    taxRegistrationNumber: t("dashboard", "taxes.manage.taxId"),
  };
  const placeholders: Record<TaxRowField, string> = {
    name: t("dashboard", "taxes.manage.namePlaceholder"),
    rate: t("dashboard", "taxes.manage.ratePlaceholder"),
    taxRegistrationNumber: t("dashboard", "taxes.manage.taxIdPlaceholder"),
  };

  const textField = (field: TaxRowField) => (
    <DashboardTextField
      size={showLabels ? "medium" : undefined}
      label={showLabels ? labels[field] : undefined}
      value={row[field]}
      onChange={(event) => onChange({ [field]: event.target.value })}
      onBlur={() => markTouched(field)}
      placeholder={placeholders[field]}
      endAdornment={field === "rate" ? <DashboardTypography variant="body2">%</DashboardTypography> : undefined}
      errorMessage={visibleError(field)}
      data-test={`${dataTestPrefix}-${DATA_TEST_SUFFIX[field]}-${row.key}`}
    />
  );

  if (!showLabels) {
    return (
      <Box sx={{ ...taxColumnsGridSx, alignItems: "start", width: "100%" }}>
        {textField("name")}
        {textField("rate")}
        {textField("taxRegistrationNumber")}
        <Stack direction="row" justifyContent="flex-end" alignItems="center" height={38}>
          {endAction}
        </Stack>
      </Box>
    );
  }

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap alignItems="flex-start" width="100%">
      <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }}>{textField("name")}</Stack>
      <Stack sx={{ width: { xs: "100%", md: 120 }, flexShrink: 0 }}>{textField("rate")}</Stack>
      <Stack sx={{ flex: 1, minWidth: 0, width: "100%" }}>{textField("taxRegistrationNumber")}</Stack>
      {endAction && (
        <Stack direction="row" alignItems="center" sx={{ height: 38, marginTop: { md: "24px" } }}>
          {endAction}
        </Stack>
      )}
    </Stack>
  );
};
